import clsx from "clsx";
import Viewer from "react-viewer";
import { MouseEvent, MouseEventHandler, useState } from "react";
import { THUMBNAILS_PREPEND } from "#env/env-vars";

import * as styles from "./image-viewer.module.scss";

interface ImageLinkProps {
  image: {
    path: string;
    name: string;
  };
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

function ImageLink({ image, onClick }: ImageLinkProps) {
  return (
    <div className={styles.imageContainer}>
      <a className={styles.imageLink} href={`https://kemono.su/data${image.path}`} onClick={onClick}>
        <img src={`${THUMBNAILS_PREPEND}/thumbnail/data${image.path}`} alt={image.name} />
      </a>
    </div>
  );
}

interface ImageListProps {
  images: {
    path: string;
    name: string;
  }[];
  onClick: (index: number) => MouseEventHandler;
}

function ImageList({ images, onClick }: ImageListProps) {
  switch (images.length) {
    case 1:
      return (
        <div className={styles.single}>
          <ImageLink image={images[0]} onClick={onClick(0)} />
        </div>
      );
    case 2:
      return (
        <div className={styles.oneByTwoGrid}>
          <ImageLink image={images[0]} onClick={onClick(0)} />
          <ImageLink image={images[1]} onClick={onClick(1)} />
        </div>
      );
    case 3:
      return (
        <div className={styles.threeByThreeGrid}>
          {images.map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
        </div>
      );
    case 4:
      return (
        <div className={styles.twoByTwoGrid}>
          {images.map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
        </div>
      );
    case 5:
      return (
        <>
          <div className={styles.oneByTwoGrid}>
            <ImageLink image={images[0]} onClick={onClick(0)} />
            <ImageLink image={images[1]} onClick={onClick(1)} />
          </div>
          <div className={styles.threeByThreeGrid}>
            {images.slice(2).map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
          </div>
        </>
      );
    case 6:
      return (
        <div className={styles.threeByThreeGrid}>
          {images.map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
        </div>
      );
    case 7:
      return (
        <>
          <div className={styles.oneByOneGrid}>
            <ImageLink image={images[0]} onClick={onClick(0)} />
          </div>
          <div className={styles.threeByThreeGrid}>
            {images.slice(1).map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
          </div>
        </>
      );
    case 8:
      return (
        <>
          <div className={styles.oneByTwoGrid}>
            <ImageLink image={images[0]} onClick={onClick(0)} />
            <ImageLink image={images[1]} onClick={onClick(1)} />
          </div>
          <div className={styles.threeByThreeGrid}>
            {images.slice(2).map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
          </div>
        </>
      );
    case 9:
      return (
        <div className={styles.threeByThreeGrid}>
          {images.map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
        </div>
      );
    case 10:
      return (
        <>
          <div className={styles.oneByOneGrid}>
            <ImageLink image={images[0]} onClick={onClick(0)} />
          </div>
          <div className={styles.threeByThreeGrid}>
            {images.slice(2).map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
          </div>
        </>
      );
    default:
      return (
        <>
          <div className={styles.threeByThreeGrid}>
            {images.slice(0, 8).map((image, index) => <ImageLink image={image} onClick={onClick(index)} />)}
            <div className={styles.moreImages} onClick={onClick(0)}>{`+ ${images.length - 8} more`}</div>
          </div>
        </>
      );
  }
}

interface IProps {
  images: { path: string, name: string }[];
  className?: string;
}

export function ImageViewer({ images, className }: IProps) {
  const [viewer, setViewer] = useState<{ visible: boolean, defaultIndex?: number }>({ visible: false });

  function imageClicked(index: number) {
    return function (e: MouseEvent<HTMLAnchorElement>) {
      e.preventDefault();
      setViewer({ visible: true, defaultIndex: index });
    };
  }

  return (
    <div className={clsx(styles.imageViewer, className)}>
      <div className={styles.imageList}>
        <ImageList images={images} onClick={imageClicked} />
      </div>

      <Viewer
        className="attachment-image-viewer"
        visible={viewer.visible}
        onClose={() => setViewer({ visible: false })}
        onMaskClick={() => setViewer({ visible: false })}
        images={images.map(({ name, path }) => ({ src: "https://kemono.su/data" + path, alt: name }))}
        downloadable
      />
    </div>
  );
}
